import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import PrivateRoute from "./Routes/PrivateRoute";
import "./App.css";
import "./assets/css/style.css";
import "./assets/css/v2/style.css";
import "./assets/css/v2/event.css";
import "./assets/css/v2/overrides.css";
import EventsListPage from "./Pages/Users/Events/EventsListPage";
import WishlistPage from "./Pages/Users/Events/WishlistPage";
import UserEventDetailPage from "./Pages/Users/Events/EventDetailPage";
import MyEventsPage from "./Pages/Organizer/EventMgt/MyEventsPage";
import PromocodePage from "./Pages/Organizer/Promocode/PromocodePage";
import Auth from "./Components/Common/Auth";
import BasicInfoPage from "./Pages/Organizer/EventMgt/BasicInfoPage";
import EventDetailPage from "./Pages/Organizer/EventMgt/EventDetailPage";
import TicketsPages from "./Pages/Organizer/EventMgt/TicketsPages";
import PurchaseTicketsPage from "./Pages/Users/Events/Checkout/PurchaseTicketsPage";
import AttendeeInformationPage from "./Pages/Users/Events/Checkout/AttendeeInformationPage";
import SettingsPage from "./Pages/Organizer/EventMgt/Settings/SettingsPage";
import InstructionsPage from "./Pages/Organizer/EventMgt/Settings/InstructionsPage";
import SettingEmailPage from "./Pages/Organizer/EventMgt/Settings/SettingEmailPage";
import PublishPage from "./Pages/Organizer/EventMgt/PublishPage";
import OrderListPage from "./Pages/Organizer/OrderMgt/OrderListPage";
import OrderDetailPage from "./Pages/Organizer/OrderMgt/OrderDetailPage";
import TicketDetailsPage from "./Pages/Organizer/OrderMgt/TicketDetailsPage";
import AttendeeListPage from "./Pages/Organizer/OrderMgt/AttendeeListPage";
import CancleRequestsPage from "./Pages/Organizer/OrderMgt/CancleRequestsPage";
import CheckoutFormPage from "./Pages/Users/Events/Checkout/CheckoutFormPage";
import AttendeeDetailsPage from "./Pages/Organizer/OrderMgt/AttendeeDetailsPage";
import SchedulePage from "./Pages/Users/Schedule/SchedulePage";
import CheckoutSuccessPage from "./Pages/Users/Events/Checkout/OrderSuccessPage";
import PaymentRefundPage from "./Pages/Organizer/OrderMgt/PaymentRefundPage";
import MyEarningPage from "./Pages/Organizer/Earning/MyEarningPage";
import UserMyEventListPage from "./Pages/Users/Events/UserMyEventListPage";
import { useEffect, useLayoutEffect } from "react";
import socket from "./utils/socketClient";
import { useSelector } from "react-redux";
import NotificationPage from "./Pages/Notification/NotificationPage";
import GuardLoginPage from "./Pages/Guard/GuardLoginPage";
import GuardEventPage from "./Pages/Guard/GuardEventPage";
import GuardAttendeeDetailsPage from "./Pages/Guard/GuardAttendeeDetailsPage";
import AssignGuardPage from "./Pages/Organizer/AssignGuard/AssignGuardPage";
import AssignPromoterPage from "./Pages/Organizer/EventMgt/AssignPromoterPage";
import PromoterEarningPage from "./Pages/Promoter/PromoterEarningPage";
import MyPromotingEventsPage from "./Pages/Promoter/MyPromotingEventsPage";
import SearchEventPage from "./Pages/Users/Events/SearchEventPage";
import EarningDetailsPage from "./Pages/Organizer/Earning/EarningDetailsPage";
import EarningAttendeeList from "./Components/Earning/EarningAttendeeList";
import RecievedOrderPage from "./Pages/Organizer/OrderMgt/RecievedOrderPage";
import { UserProfile } from "./Pages/Users/UserProfile";
import { UserPromotingEvents } from "./Pages/Users/UserPromotingEvents";
import EarningDetailsPageCommission from "./Pages/Organizer/Earning/EarningDetailsPageCommission";
import EarningAttendeeListCommission from "./Components/Earning/EarningAttendeeListCommission";
import PageNotFound from "./Components/Common/PageNotFound.js";
import AddonsPage from "./Pages/Organizer/EventMgt/AddonsPage.js";
import ErrorFallback from "./Components/Common/ErrorFallback.js";
import PublicEventDetailPage from "./Pages/Users/Events/PublicEventDetailPage";
import SsoQueryTokenLogin from "./Components/Common/SsoQueryTokenLogin";

/** Document is the scroller on event pages; reset it on every new route. */
function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document
      .querySelectorAll("#event-main-scroll, .event-main-panel, .event-main-body")
      .forEach((node) => {
        node.scrollTop = 0;
      });
  }, [pathname]);

  return null;
}

function App() {
  //const DynamicRoute = [{ Component: 'MyEvents', path: '/MyEvents' }, { Component: 'Counter', path: '/counter' }]
  const { userDetails } = useSelector((state) => state.user);
  useEffect(() => {
    socket.on("connect", () => {
      console.log(`Connected to socket server: ${socket.id}`);
    });

    socket.emit("join", userDetails);
    socket.on("disconnect", () => {
      console.log("disconnected");
    });
  }, [socket]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <BrowserRouter basename="/event">
        <ScrollToTop />
        <SsoQueryTokenLogin />
        <Routes>
          {/* Public: SSO handoff + guard login. Public browsing lives in nextevent. */}
          <Route path="/Auth" element={<Auth />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/Auth/:Token" element={<Auth />} />
          <Route path="/auth/:Token" element={<Auth />} />
          {/* Double-base64 keys from Merch/Classes contain "/", so the token can span segments. */}
          <Route path="/Auth/*" element={<Auth />} />
          <Route path="/auth/*" element={<Auth />} />
          <Route path="/guard/login" element={<GuardLoginPage />} />
          <Route
            path="/pub-access/details/:eventId"
            element={<PublicEventDetailPage />}
          />

          {/* Guard-only pages */}
          <Route element={<PrivateRoute guard="true" />}>
            <Route path="/guard/attendees" element={<GuardEventPage />} />
            <Route
              path="/guard/attendees/:attendeeId"
              element={<GuardAttendeeDetailsPage />}
            />
          </Route>

          {/* All other event pages require a logged-in user */}
          <Route element={<PrivateRoute />}>
            <Route path="/" element={<EventsListPage />} />
            <Route
              path={"/event-details/:eventId"}
              element={<UserEventDetailPage />}
            />
            <Route
              path={"/:userName/event-details/:eventId"}
              element={<UserEventDetailPage />}
            />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/:userName/userEvents" element={<UserProfile />} />
            <Route
              path="/:userName/userPromotingEvents"
              element={<UserPromotingEvents />}
            />
            <Route
              path="/purchase-tickets/:eventId"
              element={<PurchaseTicketsPage />}
            />
            <Route
              path="/attendee-information/:eventId"
              element={<AttendeeInformationPage />}
            />
            <Route path="/checkout/:eventId" element={<CheckoutFormPage />} />
            <Route path="/promocodes" element={<PromocodePage />} />
            <Route
              path="/organizer/myEarning/ticketType/attendeeList/commission"
              element={<EarningAttendeeListCommission />}
            />
            <Route
              path="/promoter/earning/:eventId"
              element={<EarningDetailsPageCommission />}
            />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/myOrders" element={<OrderListPage />} />
            <Route path="/recivedOrders" element={<RecievedOrderPage />} />
            <Route path="/myOrder/:orderId" element={<OrderDetailPage />} />
            <Route
              path="/myOrder/ticket/:ticketId/:attendeeId/:orderId"
              element={<TicketDetailsPage />}
            />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/MyEvents" element={<UserMyEventListPage />} />
            <Route path="/MyEvents/visited" element={<UserMyEventListPage />} />
            <Route
              path="/MyEvents/cancelled"
              element={<UserMyEventListPage />}
            />
            <Route path="/notifications" element={<NotificationPage />} />
            <Route path="/promoter/earning" element={<PromoterEarningPage />} />
            <Route
              path="/promoter/events"
              element={<MyPromotingEventsPage />}
            />
            <Route path="/search" element={<SearchEventPage />} />

            {/* Organizer-only pages */}
            <Route element={<PrivateRoute organizer="true" />}>
              <Route path="/organizer/MyEvents" element={<MyEventsPage />} />
              <Route
                path="/myEvents/create/basic_info"
                element={<BasicInfoPage />}
              />
              <Route
                path="/myEvents/:eventId/basic_info"
                element={<BasicInfoPage />}
              />
              <Route
                path="/myEvents/:eventId/details"
                element={<EventDetailPage />}
              />
              <Route
                path="/myEvents/:eventId/tickets"
                element={<TicketsPages />}
              />
              <Route
                path="/myEvents/:eventId/settings"
                element={<SettingsPage />}
              />
              <Route
                path="/myEvents/:eventId/settings/instructions"
                element={<InstructionsPage />}
              />
              <Route
                path="/myEvents/:eventId/settings/email-sms"
                element={<SettingEmailPage />}
              />
              <Route
                path="/myEvents/:eventId/addons"
                element={<AddonsPage />}
              />
              <Route
                path="/myEvents/:eventId/publish"
                element={<PublishPage />}
              />

              <Route
                path="/organizer/attendees"
                element={<AttendeeListPage />}
              />
              <Route
                path="/organizer/cancelTicket/requests"
                element={<CancleRequestsPage />}
              />
              <Route
                path="/organizer/attendees/:attendeeId"
                element={<AttendeeDetailsPage />}
              />
              <Route
                path="/payment/refund/:ticketId/:orderId"
                element={<PaymentRefundPage />}
              />
              <Route
                path="/organizer/myEarning"
                element={<MyEarningPage />}
              />
              <Route
                path="/organizer/assign/guard"
                element={<AssignGuardPage />}
              />
              <Route
                path="/organizer/assign/promoter/:eventId"
                element={<AssignPromoterPage />}
              />
              <Route
                path="/organizer/myEarning/:eventId"
                element={<EarningDetailsPage />}
              />
              <Route
                path="/organizer/myEarning/ticketType/attendeeList"
                element={<EarningAttendeeList />}
              />
            </Route>

            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
